import React, { useState, useEffect, useContext } from 'react';

const AuthContext = React.createContext({
    currentUser: null,
    setCurrentUser: () => {},
    isLoggedIn: false,
    id: '',
    getUser: () => null,
    switchAccount: () => {},
    setViewAs: () => {},
    login: () => {},
    logout: () => {},
    signUp: () => {},
    forgotPassword: () => {},
    updateCurrentUser: () => {},
    dashboardPartsSearch: false,
    setDashboardPartsSearch: () => {},
});

export function useAuth() {
    return useContext(AuthContext);
}

function keepIdentity(prev, fallback = {}) {
    const base = prev || {};
    return {
        Id: base.Id ?? fallback.Id ?? '',
        Name: base.Name ?? fallback.Name ?? '',
        Email: base.Email ?? fallback.Email ?? '',
        Account: {
            Id: base?.Account?.Id ?? fallback?.Account?.Id ?? '',
            Name: base?.Account?.Name ?? fallback?.Account?.Name ?? '',
            inscor__Customer_Type1__c:
                base?.Account?.inscor__Customer_Type1__c ?? fallback?.Account?.inscor__Customer_Type1__c ?? '',
        },
        Owner: {
            Name: base?.Owner?.Name ?? fallback?.Owner?.Name ?? '',
            Email: base?.Owner?.Email ?? fallback?.Owner?.Email ?? '',
        },

        userInfoAll: base.userInfoAll ?? fallback.userInfoAll ?? [],
        userInfoAllEmail: base.userInfoAllEmail ?? fallback.userInfoAllEmail ?? [],
    };
}

export const AuthContextProvider = (props) => {
    const [currentUser, setCurrentUser] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dashboardPartsSearch, setDashboardPartsSearch] = useState(false);

    const isLoggedIn = !!currentUser;
    const id = currentUser?.Id ?? '';
    const getUser = () => currentUser;

    function switchAccount(contactOrFn) {
        setCurrentUser((prev) => {
            const patch = typeof contactOrFn === 'function' ? contactOrFn(prev) : contactOrFn || {};

            const identity = keepIdentity(patch || prev, prev);
            const merged = {
                ...identity,
                ...prev,
                ...patch,
                userInfoAll: prev?.userInfoAll ?? [],
                userInfoAllEmail: prev?.userInfoAllEmail ?? [],
                tokenId: prev?.tokenId?.includes(' ') ? prev.tokenId : `Bearer ${prev?.Id}${prev?.Email}`,
            };

            updateUserLocalStorage(merged);
            return merged;
        });
    }

    function updateCurrentUser(newUser) {
        const now = new Date();

        setCurrentUser((prev) => {
            const merged = {
                ...prev,
                ...newUser,
                tokenId: prev?.tokenId,
            };
            updateUserLocalStorage(merged, now);
            return merged;
        });
    }

    function logout() {
        setCurrentUser(false);
        localStorage.clear();
        return;
    }

    function updateUserLocalStorage(data, now, firstLogin, expirationDate) {
        if (!data) return;

        localStorage.setItem('Id', data.Id || '');
        localStorage.setItem('Name', data.Name || '');
        localStorage.setItem('Email', data.Email || '');

        now && localStorage.setItem('date', now);
        expirationDate && localStorage.setItem('expirationDate', expirationDate);
        firstLogin && localStorage.setItem('firstLogin', firstLogin);

        if (data.Account) {
            localStorage.setItem('accountId', data.Account.Id || '');
            localStorage.setItem('accountName', data.Account.Name || '');
            localStorage.setItem('customerType', data.Account.inscor__Customer_Type1__c || '');
        }

        if (data.Owner) {
            localStorage.setItem('accountOwner', data.Owner.Name || '');
            localStorage.setItem('accountOwnerEmail', data.Owner.Email || '');
        }

        localStorage.setItem('noQuote', data.CP_Allow_Portal_No_Quote__c ?? '');
        localStorage.setItem('userInfoAll', JSON.stringify(data?.userInfoAll || []));
        localStorage.setItem('userInfoAllEmail', JSON.stringify(data?.userInfoAllEmail || []));
    }

    function login(data, remembered, firstLogin, opts = {}) {
        if (!data || !data.Id) {
            console.error('AuthContext.login called without valid user data:', data);
            return;
        }
        const authId = data.Id;
        try {
            const merged = {
                ...data,
                userInfoAll: data?.userInfoAll ?? currentUser?.userInfoAll ?? [],
                userInfoAllEmail: data?.userInfoAllEmail ?? currentUser?.userInfoAllEmail ?? [],
                firstLogin,
                tokenId: `Bearer ${data.Id}${data.Email}`,
            };

            setCurrentUser(merged);

            const now = new Date();
            let expirationDate;
            if (remembered) {
                expirationDate = new Date(now.getTime() + 15770000000);
            } else {
                expirationDate = new Date(now.getTime() + 999900000);
            }

            updateUserLocalStorage(merged, now, firstLogin, expirationDate);
            localStorage.setItem('authId', authId);

            return;
        } catch (error) {
            console.error('Error during AuthContext.login:', error);
        }
    }

    function signUp(email, password) {
        return;
    }

    function forgotPassword(email) {
        return;
    }

    useEffect(() => {
        setLoading(true);

        const storedId = localStorage.getItem('Id');
        const storedName = localStorage.getItem('Name');
        const storedEmail = localStorage.getItem('Email');
        const storedFirstLogin = localStorage.getItem('firstLogin');
        const storedAccountId = localStorage.getItem('accountId');
        const storedAccountName = localStorage.getItem('accountName');
        const storedAccountOwner = localStorage.getItem('accountOwner');
        const storedAccountOwnerEmail = localStorage.getItem('accountOwnerEmail');
        const storedNoQuote = localStorage.getItem('noQuote');
        const storedCustomerType = localStorage.getItem('customerType');
        const storedExperationDate = new Date(localStorage.getItem('expirationDate'));
        const storedUserInfoAll = safeParseLocalStorage('userInfoAll', []);
        const storedUserInfoAllEmail = safeParseLocalStorage('userInfoAllEmail', []);

        const storedAuthId = localStorage.getItem('authId');

        const now = new Date();

        if (storedId && storedExperationDate >= now) {
            const hydrated = keepIdentity({
                Id: storedId,
                Name: storedName,
                firstLogin: storedFirstLogin,
                Email: storedEmail,
                allowNoQuote: storedNoQuote,
                Account: {
                    Id: storedAccountId,
                    Name: storedAccountName,
                    inscor__Customer_Type1__c: storedCustomerType,
                },
                Owner: {
                    Name: storedAccountOwner,
                    Email: storedAccountOwnerEmail,
                },
                userInfoAll: storedUserInfoAll,
                userInfoAllEmail: storedUserInfoAllEmail,
            });

            hydrated.tokenId = `Bearer ${hydrated.Id}${hydrated.Email || ''}`;

            setCurrentUser(hydrated);
        }

        setLoading(false);
    }, []);

    function safeParseLocalStorage(key, fallback = []) {
        if (typeof window === 'undefined' || !window.localStorage) {
            return fallback;
        }

        try {
            const raw = localStorage.getItem(key);
            if (!raw) return fallback;
            return JSON.parse(raw);
        } catch (err) {
            console.warn(`Failed to parse localStorage key "${key}", using fallback.`, err);
            return fallback;
        }
    }

    const contextValue = {
        currentUser,
        setCurrentUser,
        isLoggedIn,
        id,
        getUser,
        switchAccount,
        setViewAs: switchAccount,
        dashboardPartsSearch,
        setDashboardPartsSearch,
        forgotPassword,
        login,
        logout,
        signUp,
        updateCurrentUser,
    };

    return <AuthContext.Provider value={contextValue}>{!loading && props.children}</AuthContext.Provider>;
};

export default AuthContext;
